import { useCallback, useMemo, useState } from "react";
import { offersForTicket } from "../utils/offers";
import { round2 } from "../utils/money";

const NO_DISCOUNT = { type: "percentage", value: 0 };

/**
 * The sale being rung up: its lines, its order level discount and the totals.
 *
 * A line keeps the product snapshot taken from the catalog so the register can
 * show names and prices instantly; the backend recomputes prices on checkout.
 */
export const useTicket = () => {
  const [lines, setLines] = useState([]);
  const [discount, setDiscount] = useState(NO_DISCOUNT);
  const [customerLabel, setCustomerLabel] = useState("");

  const addProduct = useCallback((product) => {
    setLines((current) => {
      const existing = current.find((line) => line.product.id === product.id);

      if (existing) {
        return current.map((line) =>
          line.product.id === product.id
            ? { ...line, quantity: line.quantity + 1 }
            : line,
        );
      }

      return [...current, { product, quantity: 1 }];
    });
  }, []);

  const setQuantity = useCallback((productId, quantity) => {
    setLines((current) =>
      quantity <= 0
        ? current.filter((line) => line.product.id !== productId)
        : current.map((line) =>
            line.product.id === productId ? { ...line, quantity } : line,
          ),
    );
  }, []);

  const removeProduct = useCallback((productId) => {
    setLines((current) =>
      current.filter((line) => line.product.id !== productId),
    );
  }, []);

  const reset = useCallback(() => {
    setLines([]);
    setDiscount(NO_DISCOUNT);
    setCustomerLabel("");
  }, []);

  const restore = useCallback((ticket) => {
    setLines(ticket.lines || []);
    setDiscount(ticket.discount || NO_DISCOUNT);
    setCustomerLabel(ticket.customerLabel || "");
  }, []);

  const totals = useMemo(() => {
    const subtotal = round2(
      lines.reduce(
        (sum, line) => sum + line.product.unit_price * line.quantity,
        0,
      ),
    );

    // The shop's own promotions come off before anything the cashier types,
    // matching the order the server applies them in. Without this the till
    // quoted the pre-offer price and then charged the discounted one — the
    // customer was told the wrong number.
    const offers = offersForTicket(lines);
    const afterOffers = round2(subtotal - offers.total);

    const manualDiscount =
      discount.type === "percentage"
        ? round2((afterOffers * (Number(discount.value) || 0)) / 100)
        : round2(Math.min(Number(discount.value) || 0, afterOffers));

    return {
      subtotal,
      offerDiscount: offers.total,
      offersApplied: offers.applied,
      manualDiscount,
      discountAmount: round2(offers.total + manualDiscount),
      total: round2(afterOffers - manualDiscount),
      itemCount: lines.reduce((sum, line) => sum + line.quantity, 0),
    };
  }, [lines, discount]);

  const quantityOf = useCallback(
    (productId) =>
      lines.find((line) => line.product.id === productId)?.quantity || 0,
    [lines],
  );

  return {
    lines,
    discount,
    customerLabel,
    totals,
    addProduct,
    setQuantity,
    removeProduct,
    setDiscount,
    setCustomerLabel,
    quantityOf,
    reset,
    restore,
    isEmpty: lines.length === 0,
  };
};
